/**
 * Free tool widget: best time to post per network. Pure client-side. Times are
 * expressed in the audience's LOCAL time (publish when your audience is awake),
 * so there's no timezone math — the recommended slots already assume local time.
 * The data is a general starting point; the page copy tells users to validate
 * with their own analytics.
 */
import { useState } from 'react';

type Net = 'instagram' | 'tiktok' | 'x' | 'linkedin' | 'facebook';

const NETWORKS: { key: Net; label: string }[] = [
  { key: 'instagram', label: 'Instagram' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'x', label: 'X (Twitter)' },
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'facebook', label: 'Facebook' },
];

const DAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

/** [bestWindows summary, per-day recommended slots]. Hours in 24h local time. */
const DATA: Record<Net, { summary: string; slots: string[][] }> = {
  instagram: {
    summary: 'Entre semana de 11:00–13:00 y 19:00–21:00. Evita la madrugada.',
    slots: [
      ['11:00', '19:00'], ['11:00', '20:00'], ['11:00', '13:00'], ['11:00', '20:00'],
      ['10:00', '13:00'], ['10:00', '12:00'], ['11:00', '19:00'],
    ],
  },
  tiktok: {
    summary: 'Mañanas (07:00–09:00) y noches (19:00–22:00). Picos martes a jueves.',
    slots: [
      ['08:00', '20:00'], ['08:00', '21:00'], ['08:00', '22:00'], ['09:00', '21:00'],
      ['08:00', '19:00'], ['11:00', '19:00'], ['09:00', '19:00'],
    ],
  },
  x: {
    summary: 'Horario laboral, 08:00–10:00 y 12:00–13:00. Mejor de lunes a viernes.',
    slots: [
      ['08:00', '12:00'], ['08:00', '12:00'], ['08:00', '13:00'], ['08:00', '12:00'],
      ['08:00', '11:00'], ['09:00', '—'], ['09:00', '—'],
    ],
  },
  linkedin: {
    summary: 'Martes a jueves, 08:00–10:00 y 12:00. Floja en fin de semana.',
    slots: [
      ['08:00', '12:00'], ['08:00', '10:00'], ['08:00', '10:00'], ['08:00', '12:00'],
      ['08:00', '—'], ['—', '—'], ['—', '—'],
    ],
  },
  facebook: {
    summary: 'Entre semana de 09:00–12:00 y sobre las 15:00.',
    slots: [
      ['09:00', '15:00'], ['09:00', '13:00'], ['09:00', '15:00'], ['09:00', '13:00'],
      ['09:00', '13:00'], ['12:00', '—'], ['12:00', '—'],
    ],
  },
};

export function BestTimeToPost() {
  const [net, setNet] = useState<Net>('instagram');
  const data = DATA[net];

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {NETWORKS.map((n) => (
          <button
            key={n.key}
            onClick={() => setNet(n.key)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${
              net === n.key
                ? 'bg-brand-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {n.label}
          </button>
        ))}
      </div>

      <div className="mt-5 rounded-xl border border-brand-200 bg-brand-50/60 p-4">
        <p className="text-sm text-slate-700">
          <strong className="text-brand-800">Mejores franjas:</strong> {data.summary}
        </p>
      </div>

      <div className="mt-5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-400">
              <th className="py-2 pr-4 font-medium">Día</th>
              <th className="py-2 pr-4 font-medium">Mañana</th>
              <th className="py-2 font-medium">Tarde/Noche</th>
            </tr>
          </thead>
          <tbody>
            {DAYS.map((d, i) => (
              <tr key={d} className="border-t border-slate-100">
                <td className="py-2 pr-4 font-medium text-slate-700">{d}</td>
                <td className="py-2 pr-4 tabular-nums text-slate-600">{data.slots[i]?.[0] ?? '—'}</td>
                <td className="py-2 tabular-nums text-slate-600">{data.slots[i]?.[1] ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-xs text-slate-400">
        Horas en la hora local de tu audiencia. Son un punto de partida general; valida con tus propias analíticas.
      </p>
    </div>
  );
}
